import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography
} from "@mui/material";
import { addDoc, collection, deleteDoc, doc, getDocs, limit, orderBy, query, serverTimestamp, updateDoc } from "firebase/firestore";
import { db } from "../../firebase/config";
import { formatDate } from "../../utils/dateUtils";
import { useAuth } from "../AuthContext";

const statusLabel = (status) => {
  if (status === "subscribed") return "Abonné";
  if (status === "unsubscribed") return "Désabonné";
  return "Non défini";
};

const Newsletter = () => {
  const { currentUser } = useAuth();
  const [view, setView] = useState("subscribers");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [newsItems, setNewsItems] = useState([]);
  const [selectedNewsId, setSelectedNewsId] = useState("");
  const [sending, setSending] = useState(false);

  const subscribedCount = useMemo(() => subscribers.filter((s) => s.status === "subscribed").length, [subscribers]);

  const refresh = async () => {
    try {
      setLoading(true);
      setError("");

      const subscribersQuery = query(collection(db, "newsletter_subscribers"), orderBy("createdAt", "desc"));
      const subscribersSnapshot = await getDocs(subscribersQuery);
      setSubscribers(
        subscribersSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || null
        }))
      );

      const campaignsQuery = query(collection(db, "newsletter_campaigns"), orderBy("createdAt", "desc"));
      const campaignsSnapshot = await getDocs(campaignsQuery);
      setCampaigns(
        campaignsSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || null,
          sentAt: d.data().sentAt?.toDate?.() || null
        }))
      );

      const newsQuery = query(collection(db, "news"), orderBy("createdAt", "desc"), limit(30));
      const newsSnapshot = await getDocs(newsQuery);
      setNewsItems(
        newsSnapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
          createdAt: d.data().createdAt?.toDate?.() || null
        }))
      );
    } catch (e) {
      setError("Impossible de charger la newsletter. " + (e?.message || ""));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleSubscriberStatus = async (subscriber) => {
    try {
      const nextStatus = subscriber.status === "subscribed" ? "unsubscribed" : "subscribed";
      await updateDoc(doc(db, "newsletter_subscribers", subscriber.id), {
        status: nextStatus,
        updatedAt: serverTimestamp()
      });
      setSubscribers((prev) => prev.map((s) => (s.id === subscriber.id ? { ...s, status: nextStatus } : s)));
    } catch (e) {
      setError("Impossible de mettre à jour l’abonné. " + (e?.message || ""));
    }
  };

  const deleteSubscriber = async (subscriber) => {
    try {
      await deleteDoc(doc(db, "newsletter_subscribers", subscriber.id));
      setSubscribers((prev) => prev.filter((s) => s.id !== subscriber.id));
    } catch (e) {
      setError("Impossible de supprimer l’abonné. " + (e?.message || ""));
    }
  };

  const sendCampaign = async () => {
    if (!selectedNewsId) {
      setError("Sélectionnez une actualité à envoyer.");
      return;
    }
    try {
      setSending(true);
      setError("");

      const newsItem = newsItems.find((n) => n.id === selectedNewsId);
      const subject = newsItem?.title || "Newsletter AVG Consulting";

      await addDoc(collection(db, "newsletter_campaigns"), {
        status: "sending",
        type: "news",
        newsId: selectedNewsId,
        subject,
        createdAt: serverTimestamp(),
        createdBy: currentUser?.uid || null
      });

      setSelectedNewsId("");
      await refresh();
      setView("campaigns");
    } catch (e) {
      setError("Impossible de créer l’envoi newsletter. " + (e?.message || ""));
    } finally {
      setSending(false);
    }
  };

  const resendCampaign = async (campaign) => {
    try {
      setSending(true);
      setError("");
      await updateDoc(doc(db, "newsletter_campaigns", campaign.id), {
        status: "sending",
        resentAt: serverTimestamp()
      });
      await refresh();
    } catch (e) {
      setError("Impossible de relancer l’envoi. " + (e?.message || ""));
    } finally {
      setSending(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 3, color: "#014527", fontWeight: "bold" }}>
        Newsletter
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
        <Button
          variant={view === "subscribers" ? "contained" : "outlined"}
          onClick={() => setView("subscribers")}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Abonnés
        </Button>
        <Button
          variant={view === "campaigns" ? "contained" : "outlined"}
          onClick={() => setView("campaigns")}
          sx={{ textTransform: "none", fontWeight: 800 }}
        >
          Envois
        </Button>
        <Chip label={`${subscribedCount} abonnés`} />
        <Button onClick={refresh} sx={{ textTransform: "none" }}>
          Actualiser
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress />
        </Box>
      ) : view === "subscribers" ? (
        <Paper sx={{ p: 3, borderRadius: "10px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
          {subscribers.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: "center", py: 3 }}>
              Aucun abonné.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {subscribers.map((s, idx) => (
                <Box key={s.id}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between", py: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                          {s.email}
                        </Typography>
                        <Chip
                          size="small"
                          label={statusLabel(s.status)}
                          color={s.status === "subscribed" ? "success" : s.status === "unsubscribed" ? "default" : "warning"}
                          variant={s.status === "subscribed" ? "filled" : "outlined"}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {formatDate(s.createdAt)}
                        </Typography>
                      </Box>
                      {s.source && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Source: {s.source}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <Button variant="outlined" onClick={() => toggleSubscriberStatus(s)} sx={{ textTransform: "none" }}>
                        {s.status === "subscribed" ? "Désabonner" : "Réabonner"}
                      </Button>
                      <Button color="error" variant="outlined" onClick={() => deleteSubscriber(s)} sx={{ textTransform: "none" }}>
                        Supprimer
                      </Button>
                    </Box>
                  </Box>
                  {idx < subscribers.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3, borderRadius: "10px", boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)" }}>
          <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
            Envoyer une newsletter (basée sur une actualité)
          </Typography>
          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center", mb: 3 }}>
            <TextField
              select
              label="Choisir une actualité"
              value={selectedNewsId}
              onChange={(e) => setSelectedNewsId(e.target.value)}
              size="small"
              sx={{ minWidth: 280, flex: "1 1 280px" }}
            >
              <MenuItem value="">Sélectionner</MenuItem>
              {newsItems.map((n) => (
                <MenuItem key={n.id} value={n.id}>
                  {n.title}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" disabled={sending} onClick={sendCampaign} sx={{ textTransform: "none", fontWeight: 900 }}>
              Envoyer
            </Button>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 900, mb: 2 }}>
            Historique des envois
          </Typography>
          {campaigns.length === 0 ? (
            <Typography variant="body1" sx={{ textAlign: "center", py: 3 }}>
              Aucun envoi pour le moment.
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              {campaigns.map((c, idx) => (
                <Box key={c.id}>
                  <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", justifyContent: "space-between", py: 1.5 }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                          {c.subject || "Newsletter"}
                        </Typography>
                        <Chip
                          size="small"
                          label={c.status || "unknown"}
                          color={c.status === "sent" ? "success" : c.status === "sending" ? "warning" : c.status === "error" ? "error" : "default"}
                          variant={c.status === "sent" ? "filled" : "outlined"}
                        />
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                          {formatDate(c.createdAt)}
                        </Typography>
                        {c.sentAt && (
                          <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            · Envoyé le {formatDate(c.sentAt)}
                          </Typography>
                        )}
                      </Box>
                      {c.recipientCount != null && (
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          Destinataires: {c.recipientCount}
                        </Typography>
                      )}
                      {c.errorMessage && (
                        <Typography variant="body2" sx={{ color: "error.main" }}>
                          {c.errorMessage}
                        </Typography>
                      )}
                    </Box>

                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      {c.status !== "sending" && (
                        <Button variant="outlined" disabled={sending} onClick={() => resendCampaign(c)} sx={{ textTransform: "none" }}>
                          Relancer
                        </Button>
                      )}
                      {c.newsId && (
                        <Button
                          variant="outlined"
                          component="a"
                          href={`/news/${c.newsId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ textTransform: "none" }}
                        >
                          Voir
                        </Button>
                      )}
                    </Box>
                  </Box>
                  {idx < campaigns.length - 1 && <Divider />}
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default Newsletter;
